import { ComponentProps, JSXElementConstructor } from 'react';

import {
  GAP_STEP_CARDS_HORIZONTAL,
  NODE_WIDTH,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/constants';
import { ClickPayloadType } from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodePlusButton';
import {
  KeyedGraph,
  StepGraphExtraType,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/step-graph';
import { WorkflowRunStepDisplay } from 'dashboard/components/WorkflowEditor/WorkflowCanvas/utils';
import {
  LoopBackArrow,
  PathLeanVerticallyFirst,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/WorkflowEdges';
import { BaseEdgeProps, EdgePosition } from 'dashboard/components-lib/FlowPlotter';
import FlowPlotter from 'dashboard/components-lib/FlowPlotter/FlowPlotter';
import { WorkflowRunStepStatus } from 'dashboard/models/workflow-step';
import { WorkflowStepConditional, WorkflowStepType } from 'dashboard/models/workflow-version';

type FlowPlotterProps = ComponentProps<typeof FlowPlotter>;
type CustomComponentPropsFor<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>> =
  Omit<ComponentProps<T>, keyof BaseEdgeProps>;

enum EdgeComponent {
  UpToDown = 'up-to-down',
  LoopBack = 'loop-back',
}

interface EdgeRenderingContext {
  tokenToRunStepDisplay: Record<string, WorkflowRunStepDisplay>;
  tokensForActiveLoopBack: string[];
}

export const edgeComponents: FlowPlotterProps['edgeComponents'] = {
  [EdgeComponent.UpToDown]: PathLeanVerticallyFirst,
  [EdgeComponent.LoopBack]: LoopBackArrow,
};

const isEdgeActive = ({
  fromToken,
  toToken,
  keyedGraph,
  tokenToRunStepInfo,
}: {
  fromToken: string;
  toToken: string;
  keyedGraph: KeyedGraph;
  tokenToRunStepInfo: EdgeRenderingContext['tokenToRunStepDisplay'];
}) => {
  const fromStep = keyedGraph[fromToken].step;
  if (keyedGraph[toToken].step.type === StepGraphExtraType.ConditionalRoute) {
    return tokenToRunStepInfo[toToken]?.status === WorkflowRunStepStatus.Completed;
  } else if (
    fromStep.type === StepGraphExtraType.PlusButton &&
    fromStep.clickPayload.type === ClickPayloadType.FirstInBranch &&
    keyedGraph[fromStep.clickPayload.parentNodeToken].step.type === WorkflowStepType.Conditional
  ) {
    return (
      tokenToRunStepInfo[
        (keyedGraph[fromStep.clickPayload.parentNodeToken].step as WorkflowStepConditional)
          .conditionals[fromStep.clickPayload.branchIndex].token
      ]?.status === WorkflowRunStepStatus.Completed
    );
  } else if (fromStep.type === WorkflowStepType.Module && !fromStep.collapsed) {
    return ![WorkflowRunStepStatus.Initialized, WorkflowRunStepStatus.Canceled].includes(
      tokenToRunStepInfo[fromStep.token]?.status ?? WorkflowRunStepStatus.Initialized,
    );
  } else if (
    fromToken.endsWith('-inner') &&
    fromStep.type === StepGraphExtraType.PlusButton &&
    fromStep.clickPayload.type === ClickPayloadType.AfterStep
  ) {
    return ![WorkflowRunStepStatus.Initialized, WorkflowRunStepStatus.Canceled].includes(
      tokenToRunStepInfo[fromStep.clickPayload.previousNodeToken.replace('-inner', '')]?.status ??
        WorkflowRunStepStatus.Initialized,
    );
  } else {
    const fromStep = keyedGraph[fromToken]?.step;
    const closestToken =
      fromStep.type === StepGraphExtraType.PlusButton
        ? fromStep.clickPayload.type === ClickPayloadType.AfterStep
          ? fromStep.clickPayload.previousNodeToken
          : fromStep.clickPayload.parentNodeToken
        : fromToken;

    return tokenToRunStepInfo[closestToken]?.status === WorkflowRunStepStatus.Completed;
  }
};

export const edgesFromGraph = (
  keyedGraph: KeyedGraph,
  context: EdgeRenderingContext,
): FlowPlotterProps['edges'] =>
  Object.values(keyedGraph).flatMap(
    ({ token: fromToken, adjacents, step: fromStep, moduleToken: fromModuleToken }) =>
      adjacents.map((to, i) => {
        const toGraphNode = keyedGraph[to];
        // second adjacent is into loop step
        if (i === 1 && toGraphNode.step.type === WorkflowStepType.Loop) {
          return {
            from: fromToken,
            to,
            toPos: EdgePosition.Bottom,
            componentKey: EdgeComponent.LoopBack,
            componentProps: {
              active: context.tokensForActiveLoopBack.includes(to),
              fromXToLeftVertical:
                (toGraphNode.adjacentBreadths[1] * (NODE_WIDTH + GAP_STEP_CARDS_HORIZONTAL)) / 2 +
                NODE_WIDTH / 2,
            } satisfies CustomComponentPropsFor<typeof LoopBackArrow>,
          };
        } else {
          const active = isEdgeActive({
            fromToken,
            toToken: to,
            keyedGraph,
            tokenToRunStepInfo: context.tokenToRunStepDisplay,
          });

          // Replace a connected node with module's boundary if applicable
          const fromNodeToken =
            fromStep.type === WorkflowStepType.Output &&
            toGraphNode.step.type === StepGraphExtraType.PlusButton &&
            fromModuleToken
              ? `boundary-${fromModuleToken}`
              : fromToken;
          const toNodeToken =
            toGraphNode.step.type === WorkflowStepType.Module &&
            fromStep.type === StepGraphExtraType.PlusButton &&
            !toGraphNode.step.collapsed
              ? `boundary-${to}`
              : to;

          return {
            from: fromNodeToken,
            to: toNodeToken,
            z: active ? 6 : 5,
            componentKey: EdgeComponent.UpToDown,
            fromPos:
              fromStep.type === StepGraphExtraType.PlusButton
                ? EdgePosition.Center
                : EdgePosition.Bottom,
            toPos:
              toGraphNode.step.type === StepGraphExtraType.PlusButton
                ? EdgePosition.Center
                : EdgePosition.Top,
            componentProps: {
              active,
            } satisfies CustomComponentPropsFor<typeof PathLeanVerticallyFirst>,
          };
        }
      }),
  );
